import React, { FC, useEffect } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import Animated, { useSharedValue, useAnimatedStyle, withTiming, runOnJS } from 'react-native-reanimated';

export interface AccordionRowProps {
  children: React.ReactNode;
  isExpanded: boolean;
  isClosing: boolean;
  isLoading?: boolean;
  expectedHeight: number;
  contentHeight: number;
  minHeight?: number;
  onAnimationComplete?: () => void;
  style?: StyleProp<ViewStyle>;
}

const AccordionRow: FC<AccordionRowProps> = ({ children, isExpanded, isClosing, isLoading, expectedHeight, contentHeight, onAnimationComplete, style }) => {
  const height = useSharedValue(0);
  const opacity = useSharedValue(0);

  useEffect(() => {
    if (isExpanded && !isClosing) {
      // Use expectedHeight for immediate animation
      // This gives instant feedback when user clicks
      if (expectedHeight > 0) {
        height.value = withTiming(expectedHeight, { duration: 250 }, (finished) => {
          if (finished && onAnimationComplete) {
            runOnJS(onAnimationComplete)();
          }
        });
        opacity.value = withTiming(1, { duration: 200 });
      }
    } else if (isClosing) {
      // Closing animation
      height.value = withTiming(0, { duration: 250 }, (finished) => {
        if (finished && onAnimationComplete) {
          runOnJS(onAnimationComplete)();
        }
      });
      opacity.value = withTiming(0, { duration: 200 });
    }
  }, [isExpanded, isClosing, expectedHeight]);

  const animatedStyle = useAnimatedStyle(() => ({
    height: height.value,
    opacity: opacity.value,
    overflow: 'hidden',
  }));

  return <Animated.View style={[animatedStyle, style]}>{children}</Animated.View>;
};

export default AccordionRow;
